import * as React from 'react';

import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {Label} from 'shells/form/label';
import {PathnameField} from 'shells/pathname-field/pathname-field';
import {Sidebar} from 'shells/sidebar/sidebar';
import type {SubdomainOption} from 'shells/subdomain-select/subdomain-select';
import {DescriptionBody} from 'shells/typography/description-body';

import type {SubdomainFragment as Subdomain} from 'nutshell-graphql-types';

import {useGetSubdomains, SubdomainSelectWrapper} from '../../core/subdomains';

import {useUpdatePage} from '../graphql/hooks';

import styles from './domain-sidebar.module.css';

type Props = {
    onClose: () => void;
    pageId: string;
    urlSlug: string;
    subdomain: Subdomain | null | undefined;
};

export function DomainSidebar(props: Props) {
    const [selectedDomain, setSelectedDomain] = React.useState<SubdomainOption | null | undefined>(
        props.subdomain && {
            value: props.subdomain.fromDomainId,
            label: props.subdomain.formattedRecord,
            isVerified: props.subdomain.isVerified,
        }
    );
    const [urlSlug, setUrlSlug] = React.useState<string>(props.urlSlug);
    const [componentMounted, setComponentMounted] = React.useState(false);

    const {subdomains, error: errorGettingSubdomains} = useGetSubdomains();
    const {updatePage, isLoading: isSaving, error: errorSaving} = useUpdatePage();

    const hasUpdatedSubdomain =
        (props.subdomain &&
            selectedDomain &&
            props.subdomain.fromDomainId !== selectedDomain.value) ||
        props.subdomain !== selectedDomain;
    const hasUpdatedUrlSlug = props.urlSlug !== urlSlug;

    const handleUpdatePage = () => {
        updatePage({
            pageId: props.pageId,
            ...(hasUpdatedSubdomain
                ? {fromDomainId: selectedDomain ? selectedDomain.value : null}
                : {}),
            ...(hasUpdatedUrlSlug ? {publicSlug: urlSlug} : {}),
        }).then(() => {
            props.onClose();
        });
    };

    React.useEffect(() => {
        // React portal for sidebar screws up pathname field without this
        setComponentMounted(true);
    }, []);

    return (
        <Sidebar
            isOpen={true}
            onClose={props.onClose}
            headerTitle='Use custom domain'
            onSave={handleUpdatePage}
            submitDisabled={!(hasUpdatedSubdomain || hasUpdatedUrlSlug) || !urlSlug || isSaving}
            isSaving={isSaving}
        >
            {componentMounted && (
                <div className={styles['custom-domain-sidebar']}>
                    {errorSaving && (
                        <FlashBanner type='error' isOverlay={true} duration={3000}>
                            {errorSaving}
                        </FlashBanner>
                    )}
                    <DescriptionBody>
                        Nutshell provides a default domain for all landing pages. If you’d like to
                        use your own domain for this page, select or add it below.
                    </DescriptionBody>
                    <SubdomainSelectWrapper
                        label='Domain'
                        selectedOption={selectedDomain}
                        onSelectOption={(newSelection?: SubdomainOption | null) =>
                            setSelectedDomain(newSelection)
                        }
                        options={
                            subdomains
                                ? subdomains.map((subdomain) => ({
                                      value: subdomain.fromDomainId,
                                      label: subdomain.formattedRecord,
                                      isVerified: subdomain.isVerified,
                                  }))
                                : undefined
                        }
                        isClearable={true}
                        showUnverifiedHelpText={true}
                        withCreateNewOption={true}
                        isDisabled={Boolean(errorGettingSubdomains)}
                        helpText={
                            errorGettingSubdomains && (
                                <div className={styles['error-text']}>
                                    Oh no! Something went wrong. Please try again later.
                                </div>
                            )
                        }
                    />
                    <div>
                        <Label htmlFor='pathname-input-field'>URL path</Label>
                        <PathnameField
                            domainName={
                                (selectedDomain && selectedDomain.label) ||
                                `https://${NutshellFrontendSettings.urlShortenerRoot}/${NutshellFrontendSettings.instanceId}/`
                            }
                            value={urlSlug}
                            onChange={(newValue) => {
                                setUrlSlug(newValue);
                            }}
                        />
                    </div>
                </div>
            )}
        </Sidebar>
    );
}
